import { useMutation, useQuery } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client/core';
import {
  ALL_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  getHeaderValue,
  getTranslatedText,
  Loader,
  SecondaryButton,
  toast,
  useIntl
} from '@nile/components';
import withLaunchDarkly from '@nile/components/src/HOC/withLaunchDarkly.js';
import {
  GET_ALL_APPROVAL_STATUS,
  GET_FIELD,
  GET_PUBLIC_APPROVAL,
  UPDATE_APPROVAL
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { AppProvider } from '@shopify/polaris';
import { Warning } from '@styled-icons/material/Warning';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Heading,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { createHttpLink } from 'apollo-link-http';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { Tooltip } from 'react-clone-tippy';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

const InvoiceTemplateWrap = styled.div`
  background-image: url(${envConfig.imageRoot}svg/chatBg.svg);
  height: 100vh;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: 20px;
  .Superops-Card {
    padding: 40px;
    height: fit-content;
    width: 700px;
  }

  .comment-card .Superops-Card {
    padding: 10px 20px;
    height: unset;
    width: 100%;
  }
`;

const Warn = styled(Warning)`
  height: 12px;
  width: 12px;
  min-height: 12px;
  min-width: 12px;
  margin-right: 8px;
`;

const ApprovalWidgetWrapper = styled.div``;
const CommentBox = styled.div`
  margin-top: 15px;
`;
const ApprovalActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const StyledTag = styled.div`
  padding: 2px 10px;
  color: #ffffff;
  background: ${(props) => props.background};
  border-radius: 4px;
  max-width: 140px
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  //mix-blend-mode: darken;
`;

const Subject = styled.div`
  margin-left: 8px;
  .subject {
    line-height: 30px !important;
    font-size: 20px !important;
    font-weight: bold;
  }
`;

const Value = styled.div`
  line-height: 16px;
  margin-left: 8px;
  font-weight: bold;
  color: black;
  white-space: nowrap;
  max-width: 250px;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const cache = new InMemoryCache({
  addTypename: false
});

function ApprovalTemplate(props) {
  const params = new URLSearchParams(props.location.search);
  const approvalId = params.get('approvalId');
  const token = params.get('token');
  const AccountClient = new ApolloClient({
    cache,
    link: createHttpLink({
      uri: localStorage?.getItem('apiDomain') + envConfig.accountsGateway,
      credentials: 'include',
      headers: {
        ...getHeaderValue(),
        authorization: `Bearer nl-${token}`
      }
    }),
    connectToDevTools: true
  });
  const TicketClient = new ApolloClient({
    cache,
    link: createHttpLink({
      uri: localStorage?.getItem('apiDomain') + envConfig.ticketGateway,
      credentials: 'include',
      headers: {
        ...getHeaderValue(),
        authorization: `Bearer nl-${token}`
      }
    }),
    connectToDevTools: true
  });
  return (
    <AppProvider>
      <ApprovalWithFlags
        approvalId={approvalId}
        token={token}
        AccountClient={AccountClient}
        TicketClient={TicketClient}
      />
    </AppProvider>
  );
}
function Approval(props) {
  const { clientId, approvalId, token, AccountClient, TicketClient } = props;
  const flags = useFlags();
  const intl = useIntl();
  const runbookRevampFlag = flags?.unifiedRunbooks;
  const {
    data: approvalData,
    loading: approvalLoading,
    error: approvalError
  } = useQuery(GET_PUBLIC_APPROVAL, {
    client: TicketClient,
    variables: {
      approvalId: approvalId
    }
  });
  const {
    data: priorityValues,
    loading: priorityLoading,
    error: priorityError
  } = useQuery(GET_FIELD, {
    client: AccountClient,
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY
      }
    }
  });
  if (approvalLoading || priorityLoading)
    return (
      <InvoiceTemplateWrap>
        <div className="fhp w100 flex actr jctr">
          <Loader />
        </div>
      </InvoiceTemplateWrap>
    );
  if (approvalError || priorityError) return <div>error...</div>;
  const colors = {};
  const fldOptions =
    priorityValues?.getField?.options || priorityValues?.getField?.fieldOptions;
  fldOptions?.forEach((el) => {
    colors[el.value] = el?.details?.colourCode;
  });
  const { ticket, status } = approvalData.getPublicApproval;

  const redirectToTicket = () => {
    const origin = window.location.origin;
    const ticketId = ticket?.ticketId;
    const constructedUrl = `${origin}/#/tickets/${ticketId}/ticket`;
    window.open(constructedUrl);
  };
  return (
    <InvoiceTemplateWrap>
      <Card>
        {runbookRevampFlag ? (
          <>
            <div className="flex actr fx-sb">
              <div>
                <Badge
                  size="large"
                  icon={<SVGIcon name="WarningTriangleOutline" />}
                  iconPosition="left"
                >
                  #{ticket.displayId}
                </Badge>
              </div>
              <div>
                <SecondaryButton
                  onClick={redirectToTicket}
                  icon="TicketOutline"
                  label={getTranslatedText(intl, 'common.view.ticket')}
                />
              </div>
            </div>

            <div className="mt20 mb20">
              <Heading element="h1" weight="SemiBold">
                {ticket?.subject}
              </Heading>
            </div>

            {approvalData?.getPublicApproval?.additionalMessage && (
              <div className="w100 comment-card">
                <Card noShadow variant="attention">
                  <div className="">
                    <TextStyle>
                      {approvalData?.getPublicApproval?.additionalMessage}
                    </TextStyle>
                  </div>
                </Card>
              </div>
            )}

            <div>
              <ApprovalWidgetNew
                ticket={ticket}
                approvalId={approvalId}
                TicketClient={TicketClient}
                status={status}
              />
            </div>
          </>
        ) : (
          <>
            <div className="flex center">
              <StyledTag
                className="priority-cell"
                background={colors[ticket.priority] || '#9797974D'}
              >
                <Warn />
                {ticket.displayId}
              </StyledTag>
              <div className="ml8">
                <Subject>
                  <div className="subject">{ticket.subject}</div>
                </Subject>
                <div className="flex center info ml8">
                  <SVGIcon name="AssignToMeIcon" size="sm" />
                  <Value>{ticket.requester?.name || '--'}</Value>
                </div>
              </div>
            </div>
            <ApprovalWidget
              ticket={ticket}
              approvalId={approvalId}
              TicketClient={TicketClient}
              status={status}
            />
          </>
        )}
      </Card>
    </InvoiceTemplateWrap>
  );
}

let ApprovalWithFlags = withLaunchDarkly(Approval);

function ApprovalWidget(props) {
  const intl = useIntl();
  const { ticket, approvalId, TicketClient, status } = props;
  const [comments, setComments] = useState('');
  const [updateLoading, setLoading] = useState(false);
  const [showWidget, setShowWidget] = useState(true);
  const {
    data: approvalStatuses,
    loading: statusLoading,
    error: statusError
  } = useQuery(GET_ALL_APPROVAL_STATUS, { client: TicketClient });
  const [updateApproval] = useMutation(UPDATE_APPROVAL, {
    client: TicketClient,
    onCompleted({ updateApproval }) {
      toast('success', 'Approval status updated successfully');
      setLoading(false);
      setShowWidget(false);
    },
    onError() {
      setLoading(false);
      toast('error', 'Error occured');
    }
  });
  if (statusLoading) return '';
  if (statusError) return <div>error...</div>;
  const sentStatus = approvalStatuses.getAllApprovalStatus.find(
    (el) => el.state === 'CREATE'
  )?.name;
  const approveTicket = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'APPROVE'
    )?.name;
    setLoading(true);
    updateApproval({
      variables: {
        approvalId: approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const rejectTicket = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'REJECT'
    )?.name;
    setLoading(true);
    updateApproval({
      variables: {
        approvalId: approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const askMoreInfo = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'NEED_MORE_INFORMATION'
    )?.name;
    setLoading(true);
    updateApproval({
      variables: {
        approvalId: approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const canShow = status === sentStatus;
  const placeHolder = getTranslatedText(
    intl,
    'ticket.approvalTemplate.comments.pl'
  );
  if (showWidget && canShow) {
    return (
      <ApprovalWidgetWrapper>
        <CommentBox>
          <TextField
            value={comments}
            multiline
            name="comments"
            onChange={(val) => {
              setComments(val);
            }}
            placeholder={placeHolder}
            type="text"
          />
        </CommentBox>
        <div>
          <ApprovalActionButtons>
            <ButtonGroup>
              <Button
                onClick={() => {
                  askMoreInfo();
                }}
                disabled={updateLoading}
              >
                <FormattedMessage id="ticket.details.runbook.needMoreInfo" />
              </Button>
              <Button
                onClick={() => {
                  rejectTicket();
                }}
                disabled={updateLoading}
              >
                <FormattedMessage id="common.reject" />
              </Button>
              <Button
                onClick={() => {
                  approveTicket();
                }}
                primary
                disabled={updateLoading}
              >
                <FormattedMessage id="common.approve" />
              </Button>
            </ButtonGroup>
          </ApprovalActionButtons>
        </div>
      </ApprovalWidgetWrapper>
    );
  }
  return (
    <div className="mt10">
      <FormattedMessage id="ticket.approvalTemplate.responseSent.message" />
    </div>
  );
}

function ApprovalWidgetNew(props) {
  const { ticket, approvalId, TicketClient, status } = props;
  const intl = useIntl();
  const [comments, setComments] = useState('');
  const [updateLoading, setLoading] = useState(false);
  const [showWidget, setShowWidget] = useState(true);
  const {
    data: approvalStatuses,
    loading: statusLoading,
    error: statusError
  } = useQuery(GET_ALL_APPROVAL_STATUS, { client: TicketClient });
  const [updateApproval] = useMutation(UPDATE_APPROVAL, {
    client: TicketClient,
    onCompleted({ updateApproval }) {
      toast('success', 'Approval status updated successfully');
      setLoading(false);
      setShowWidget(false);
    },
    onError() {
      setLoading(false);
      toast('error', 'Error occured');
    }
  });
  if (statusLoading) return '';
  if (statusError) return <div>error...</div>;
  const sentStatus = approvalStatuses.getAllApprovalStatus.find(
    (el) => el.state === 'CREATE'
  )?.name;
  const approveTicket = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'APPROVE'
    )?.name;
    setLoading(true);
    updateApproval({
      variables: {
        approvalId: approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const rejectTicket = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'REJECT'
    )?.name;
    setLoading(true);
    updateApproval({
      variables: {
        approvalId: approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const askMoreInfo = () => {
    const approvalStatus = approvalStatuses.getAllApprovalStatus.find(
      (el) => el.state === 'NEED_MORE_INFORMATION'
    )?.name;
    setLoading(true);
    updateApproval({
      variables: {
        approvalId: approvalId,
        approvalInput: {
          status: approvalStatus,
          comment: comments
        }
      }
    });
  };
  const canShow = status === sentStatus;
  const placeHolder = getTranslatedText(intl, 'common.enter.remark.here');
  const label = getTranslatedText(intl, 'common.remark.optional');
  if (showWidget && canShow) {
    return (
      <ApprovalWidgetWrapper id="approval-widget-new">
        <CommentBox>
          <TextField
            value={comments}
            multiline
            name="comments"
            onChange={(val) => {
              setComments(val);
            }}
            placeholder={placeHolder}
            type="text"
            label={label}
          />
        </CommentBox>
        <div>
          <ApprovalActionButtons>
            <ButtonGroup>
              <Button
                onClick={() => {
                  askMoreInfo();
                }}
                disabled={updateLoading}
              >
                <FormattedMessage id="ticket.details.runbook.needMoreInfo" />
              </Button>
              <Tooltip
                title={getTranslatedText(intl, 'common.reject')}
                position="top-start"
                inertia
                className="full-width"
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
                arrow
              >
                <Button
                  onClick={() => {
                    rejectTicket();
                  }}
                  destructive
                  icon={<SVGIcon name="RejectedCircle" />}
                ></Button>
              </Tooltip>
              <Tooltip
                title={getTranslatedText(intl, 'common.approve')}
                position="top-start"
                inertia
                className="full-width"
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
                arrow
              >
                <Button
                  onClick={() => {
                    approveTicket();
                  }}
                  disabled={updateLoading}
                  success
                  icon={<SVGIcon name="Check" />}
                ></Button>
              </Tooltip>
            </ButtonGroup>
          </ApprovalActionButtons>
        </div>
      </ApprovalWidgetWrapper>
    );
  }
  return (
    <div className="mt10">
      <FormattedMessage id="ticket.approvalTemplate.responseSent.message" />
    </div>
  );
}

export default withRouter(ApprovalTemplate);
